import { useCopyToClipboard } from '@lfg/core/hooks';
import { Box, CircularProgress, FormControlLabel, Grid2, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { Fragment, useEffect, useRef } from 'react';
import { Controller, FormProvider, useFieldArray } from 'react-hook-form';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import { useAccount } from 'wagmi';

import AddIcon from 'src/assets/icons/add.svg';
import CloseIcon from 'src/assets/icons/close.svg';
import { DestructiveButton } from 'src/components/Buttons/DestructiveButton';
import { PrimaryButton } from 'src/components/Buttons/PrimaryButton';
import { SecondaryButton } from 'src/components/Buttons/SecondaryButton';
import { AdminNumberFormat } from 'src/components/NodeSaleForm/AdminNumberFormat';
import { AdminInput } from 'src/components/NodeSaleForm/AdminTextField';
import { SUPPLY_NODE_DEFAULT } from 'src/global/constants/otc.contant';
import { Roles } from 'src/global/constants/role.constant';
import { colors } from 'src/global/styles/colors';
import { useGA } from 'src/hooks/useGA';
import useOtcSale from 'src/hooks/useOtcSale';
import { useRoles } from 'src/provider/RoleProvider';
import { useGetOTCDetailQuery } from 'src/store/services/otc.service';
import { useGetProjectDetailQuery } from 'src/store/services/project.service';
import { generateUrlOtc } from 'src/utils/helpers';
import { isAllowedGreadterThanZero, isAllowedPercentage } from 'src/utils/numberFormat';

import CurrencySelection from './components/CurrencySelection';
import { EndSaleOtcModal, type EndSaleOtcRequestHandle } from './components/EndSaleOtcModal';
import { PublishOtcModal, type PublishOtcRequestHandle } from './components/PublishOtcModal';
import SalePeriod from './components/SalePeriod';

const OtcDetail = () => {
  const { id, otcId } = useParams();
  const { address } = useAccount();
  const [_addr, copyURL] = useCopyToClipboard();
  const publishModalRef = useRef<PublishOtcRequestHandle>(null);
  const endsaleModalRef = useRef<EndSaleOtcRequestHandle>(null);
  const { roles } = useRoles();
  const isAdmin = roles.includes(Roles.ADMIN);
  const { trackButtonClickEvent } = useGA();

  const { data: projectDetail } = useGetProjectDetailQuery(
    { id, address },
    {
      skip: !id || !address,
    },
  );
  const { data: OTCDetail, isLoading: otcLoading } = useGetOTCDetailQuery(
    { programId: id, creator: address, otcId: otcId || '' },
    {
      skip: !id || !address || !otcId,
    },
  );

  const recipients = projectDetail?.recipients.map(i => ({ ...i, percent: 0 }));
  const { form, isDetail, handleCreateOtcSale, handleDeleteOtc, loadingCreateOtc, loadingDeleteOtc } = useOtcSale({
    detail: OTCDetail,
    programId: id,
  });

  const {
    control,
    formState: { errors },
    watch,
  } = form;

  const { fields, remove, append, replace } = useFieldArray({
    control,
    name: 'recipients',
  });
  const amountNode = watch('amountNode');
  const pricePerNode = watch('pricePerNode');
  const token = watch('token');

  const recipentsRootErrorMsg = errors?.recipients?.root?.message || '';

  useEffect(() => {
    if (projectDetail) {
      replace(isDetail ? (OTCDetail?.recipients ?? []) : (recipients ?? []));
    }
  }, [isDetail, JSON.stringify(projectDetail)]);

  const onSubmit = () => {
    if (publishModalRef && publishModalRef.current) publishModalRef.current.show();
  };

  const onError = (val: any) => {
    console.log(val, 'errors');
  };

  const onCopyUrl = (url: string) => {
    copyURL(url);
    toast.success('Copied URL to clipboard.', {
      autoClose: 2000,
      position: 'top-center',
    });
  };

  const renderAction = (condition: boolean, idx: number) =>
    condition ? (
      <Typography
        color='white'
        onClick={() => append({ name: undefined, address: undefined, percent: 0 })}
        component={'p'}>
        <AddIcon />
      </Typography>
    ) : (
      <Typography color='#F04923' onClick={() => remove(idx)} component={'p'}>
        <CloseIcon />
      </Typography>
    );

  const renderFundDistribution = () => (
    <div className=''>
      <Grid2 container spacing={2} columns={12} className='bg-neutral-06 rounded-[4px] p-2'>
        <Grid2 size={3}>
          <Typography className='text-body_m font-gotham text-neutral-90'>Recipient</Typography>
        </Grid2>
        <Grid2 size={7}>
          <Typography className='text-body_m font-gotham text-neutral-90'>Wallet Address</Typography>
        </Grid2>
        <Grid2 size={2}>
          <Typography className='text-body_m font-gotham text-neutral-90'>Percentage</Typography>
        </Grid2>
      </Grid2>

      {fields && fields.length > 0
        ? fields.map((i, idx) => (
            <Grid2 container spacing={2} key={i.id} columns={12} className='p-2'>
              <Grid2 size={3}>
                <Controller
                  name={`recipients.${idx}.name`}
                  control={control}
                  render={({ field }) => <AdminInput placeholder={'Recipient Name'} disabled={isDetail} {...field} />}
                />
              </Grid2>
              <Grid2 size={7}>
                <Controller
                  name={`recipients.${idx}.address`}
                  control={control}
                  render={({ field }) => (
                    <AdminInput placeholder={'Enter Wallet Address'} disabled={isDetail} {...field} />
                  )}
                />
              </Grid2>
              <Grid2 size={2}>
                <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
                  <Controller
                    name={`recipients.${idx}.percent`}
                    control={control}
                    render={({ field }) => (
                      <AdminNumberFormat
                        isAllowed={isAllowedPercentage}
                        disabled={isDetail}
                        onValueChange={values => {
                          const percentage = values.floatValue ? Number(values.floatValue) : 0;
                          field.onChange(percentage);
                        }}
                        placeholder={'0%'}
                        {...field}
                      />
                    )}
                  />
                  {isDetail ? null : renderAction(idx === fields.length - 1, idx)}
                </Stack>
              </Grid2>
            </Grid2>
          ))
        : null}
      {recipentsRootErrorMsg ? (
        <Typography
          sx={{
            bottom: 0,
            fontWeight: 400,
            color: '#D32F2F',
          }}
          className='font-gotham'>
          {recipentsRootErrorMsg}
        </Typography>
      ) : null}
    </div>
  );

  const renderButtonDetails = () => {
    const url = generateUrlOtc(OTCDetail?.sale as string, otcId as string);

    return (
      <Stack
        flexDirection={'row'}
        marginTop={3}
        width={'100%'}
        alignItems={'center'}
        justifyContent={'space-between'}
        className='col-span-12'>
        <DestructiveButton
          sx={{ display: isAdmin ? 'flex !important' : 'none !important' }}
          onClick={() => {
            trackButtonClickEvent({ name: 'end_otc_sale', address });
            endsaleModalRef.current?.show();
          }}>
          End Sale
        </DestructiveButton>
        <Stack gap={2} flexDirection={'row'} alignItems={'center'} justifyContent={'end'}>
          <SecondaryButton onClick={() => onCopyUrl(url)}>Copy Link</SecondaryButton>
          <PrimaryButton onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>Visit Site</PrimaryButton>
        </Stack>
      </Stack>
    );
  };
  const renderButtonCreate = () => (
    <Stack marginTop={3} width={'100%'} alignItems={'end'} className='col-span-12'>
      <PrimaryButton form='otcForm' type='submit'>
        Publish Site
      </PrimaryButton>
    </Stack>
  );

  const renderContent = () => {
    if (otcLoading) {
      return (
        <Stack alignItems={'center'} justifyContent={'center'} height={'100vh'} width={'100%'}>
          <CircularProgress />
        </Stack>
      );
    }

    return (
      <Fragment>
        <Typography className='text-head_l font-gotham text-white'>Sale Settings</Typography>
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <Box
              sx={{
                width: '100%',
              }}>
              <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
                Amount of Nodes to OTC
              </Typography>
              <Controller
                name='amountNode'
                control={control}
                disabled={isDetail}
                render={({ field }) => (
                  <AdminNumberFormat
                    fixedDecimalScale={false}
                    decimalScale={1}
                    isAllowed={isAllowedGreadterThanZero}
                    onValueChange={values => {
                      field.onChange(Number(values.floatValue) || 0);
                    }}
                    isError={!!errors?.amountNode}
                    errorMsg={errors?.amountNode?.message}
                    placeholder={'0'}
                    suffix=''
                    prefix=''
                    {...field}
                  />
                )}
              />
            </Box>
          </Grid2>
          <Grid2 size={6}>
            <Box
              sx={{
                width: '100%',
              }}>
              <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
                Price per Node (in USD)
              </Typography>
              <Controller
                name='pricePerNode'
                control={control}
                disabled={isDetail}
                render={({ field }) => (
                  <AdminNumberFormat
                    allowNegative={false}
                    decimalScale={3}
                    allowLeadingZeros={false}
                    isAllowed={isAllowedGreadterThanZero}
                    onValueChange={values => {
                      field.onChange(Number(values.floatValue) || 0);
                    }}
                    isError={!!errors?.pricePerNode}
                    errorMsg={errors?.pricePerNode?.message}
                    placeholder={'0'}
                    suffix=''
                    prefix=''
                    readOnly={isDetail}
                    {...field}
                  />
                )}
              />
            </Box>
          </Grid2>
        </Grid2>
        <CurrencySelection
          token={token}
          amountNode={amountNode}
          nodeName={projectDetail?.name}
          readOnly={isDetail}
          pricePerNode={pricePerNode}
        />
        <Box width={'100%'}>
          <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
            Whitelisted Wallet Address
          </Typography>
          <Controller
            name='payerAddr'
            control={control}
            render={({ field }) => (
              <AdminInput
                isError={!!errors['payerAddr']}
                errorMsg={errors['payerAddr']?.message}
                placeholder={'Enter Wallet Address'}
                disabled={isDetail}
                {...field}
              />
            )}
          />
        </Box>
        <SalePeriod readOnly={isDetail} />
        <Typography className='text-head_s font-gotham text-white'>Node Supply Management</Typography>
        <Box width={'100%'}>
          <Controller
            control={control}
            name='supply'
            render={({ field }) => (
              <RadioGroup
                aria-disabled={isDetail}
                sx={{
                  color: 'white',
                  display: 'flex',
                  flexDirection: 'row',
                }}
                {...field}>
                {SUPPLY_NODE_DEFAULT.map(i => (
                  <FormControlLabel
                    key={i.value}
                    disabled={isDetail}
                    value={i.value}
                    sx={{
                      fontSize: '14px',
                      width: '50%',
                      fontWeight: '400',
                      '& .MuiRadio-root.Mui-disabled': {
                        color: 'white',
                      },
                      '& .MuiRadio-root.Mui-checked': {
                        color: 'white',
                      },
                    }}
                    control={
                      <Radio
                        sx={{
                          fontSize: '14px',
                          fontWeight: '400',
                          color: colors.neutral900,
                        }}
                      />
                    }
                    label={
                      <Typography fontSize={'14px'} fontWeight={400}>
                        {i.label}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>
            )}
          />
        </Box>
        <Typography className='text-head_s font-gotham text-white '>Fund Distribution</Typography>
        {renderFundDistribution()}
      </Fragment>
    );
  };

  return (
    <Box width={'100%'} height={'100%'} display={'flex'} justifyContent={'center'} minHeight={'100dvh'}>
      <Box
        sx={{
          marginTop: '40px',
          maxWidth: '728px',
          width: '100%',
        }}>
        <Typography
          textAlign={'center'}
          color={'white'}
          fontWeight={700}
          fontSize={'32px'}
          variant='h5'
          paddingBottom={3}>
          OTC SALE DETAILS
        </Typography>
        <Box className={'rounded-[16px] bg-[#ffffff07] p-8'}>
          <FormProvider {...form}>
            <form id='otcForm' onSubmit={form.handleSubmit(onSubmit, onError)} className='flex flex-col gap-y-6'>
              {renderContent()}
            </form>
          </FormProvider>
        </Box>
        {isDetail ? renderButtonDetails() : renderButtonCreate()}

        <PublishOtcModal
          ref={publishModalRef}
          handleCreateOtcSale={handleCreateOtcSale}
          loading={loadingCreateOtc}
          onPublishClick={() => trackButtonClickEvent({ name: 'publish_otc', address })}
        />
        <EndSaleOtcModal ref={endsaleModalRef} handleDeleteOtc={handleDeleteOtc} loading={loadingDeleteOtc} />
      </Box>
    </Box>
  );
};

export default OtcDetail;
